import { compressImage } from '@home/utils/image';

/**
 * Trigger Window.resize Event with deferred execution
 *
 * This implementation uses requestAnimationFrame to ensure
 * the resize event is triggered during the browser's next repaint cycle
 */
export const fireWindowResize = (() => {
  // Cache for the browser-specific resize event function
  let resizeFunction: (() => void) | null = null;

  // Flag to track if the detection has been scheduled
  let detectionScheduled = false;

  // Return the actual function that will be called when fireWindowResize is invoked
  return () => {
    // If we've already determined the browser's resize method, schedule it for execution
    if (resizeFunction) {
      requestAnimationFrame(resizeFunction);
      return;
    }

    // If detection is already scheduled, skip duplicate scheduling
    if (detectionScheduled) return;
    detectionScheduled = true;

    // Schedule browser capability detection for the next animation frame
    requestAnimationFrame(() => {
      // Browser capability detection - determine the best way to trigger resize event
      // Modern browsers (Chrome, Firefox, Edge, Safari 13+)
      if (typeof Event === 'function' && typeof window.dispatchEvent === 'function') {
        resizeFunction = () => {
          // Create and dispatch a synthetic resize event
          window.dispatchEvent(new Event('resize'));
        };
      }
      // Legacy browsers (Older Firefox, Safari <13)
      else if (typeof document.createEvent === 'function') {
        resizeFunction = () => {
          // Create event using older method
          const event = document.createEvent('HTMLEvents');
          // Initialize as resize event (bubbles and cancelable)
          event.initEvent('resize', true, true);
          window.dispatchEvent(event);
        };
      }
      // Internet Explorer (IE 5-10)
      else if (typeof (document as any).createEventObject === 'function') {
        resizeFunction = () => {
          // Use IE-specific event firing method
          (window as any).fireEvent('onresize');
        };
      }
      // Fallback for unsupported browsers
      else {
        // No supported method - use empty function as fallback
        resizeFunction = () => {};
        // Warn about lack of resize event support
        console.warn('fireWindowResize: No supported resize event implementation found');
        return;
      }

      // Execute the determined resize function after detection
      resizeFunction();
    });
  };
})(); // Immediately invoked to create closure with cached resizeFunction

/**
 * 测试文本宽度的方法，使用 canvas 的 measureText API 来获取文本的宽度
 * @param text 文本内容
 * @param font 字体样式，例如 "16px Arial"font
 * @return 文本的宽度
 */
export const measureTextWidth = (text: string, font: string): number => {
  if (!text) {
    return 0;
  }

  // SSR/测试环境兜底
  if (typeof document === 'undefined') {
    return text.length * 8;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return text.length * 8;
  }

  context.font = font;

  return context.measureText(text).width;
};

/**
 * @description 截图方法（使用 modern-screenshot 库，支持 mask-image 等现代 CSS 特性）
 * @date 2025-12-04
 * @author lv.chen
 * @param target 目标元素
 * @param fileName 文件名
 * @returns File 对象
 */
export const screenshot = async (target: HTMLElement, fileName: string) => {
  if (!target) {
    return;
  }

  // console.debug('[Screenshot] 开始截图，使用 modern-screenshot 库');
  // console.debug(`[Screenshot] 目标元素宽度: ${target.offsetWidth}px, 高度: ${target.offsetHeight}px`);

  try {
    const { domToPng } = await import(/* webpackChunkName: "modern-screenshot" */ 'modern-screenshot');

    // 计算合适的缩放比例
    // 使用 2 倍缩放以获得高清图片（适配高分辨率屏幕）
    // 如果元素宽度小于 800px，使用 2.5 倍；否则使用 2 倍
    const scale = target.offsetWidth < 800 ? 2.5 : 2;

    // console.debug(`[Screenshot] 使用缩放比例: ${scale}`);

    // 使用 modern-screenshot 的 domToPng 方法
    // modern-screenshot 对现代 CSS 特性（包括 mask-image）有更好的支持
    const dataUrl = await domToPng(target, {
      // 设置缩放比例，使用 2 倍或 2.5 倍以获得高清图片
      scale,
      // 设置质量为最高
      quality: 1,
      // 启用调试模式
      debug: false,
    });

    // console.debug('[Screenshot] 截图完成，转换为 File 对象');

    // 将 dataUrl 转换为 Blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    // 创建 File 对象
    let file = new File([blob], fileName, {
      type: 'image/png',
      lastModified: Date.now(),
    });

    // console.debug(`[Screenshot] 原始文件大小: ${Math.round(file.size / 1024)}KB`);

    // 如果文件大于 5MB，进行适度压缩（保持较高质量）
    if (file.size > 5 * 1024 * 1024) {
      // console.debug(`[Screenshot] 文件大小超过 5MB，开始压缩`);
      const compressResult = await compressImage(file, { quality: 0.85 });
      file = compressResult.file;
      // console.debug(`[Screenshot] 压缩后大小: ${Math.round(file.size / 1024)}KB`);
    }

    return file;
  } catch (error) {
    console.error('[Screenshot] 截图失败:', error);
    throw error;
  }
};
